import React, { useEffect, useRef, useState } from "react";
import { Inertia } from "@inertiajs/inertia";
import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import AppLayout from "@/Layouts/AppLayout";
import OrganizationLayout from "@/Layouts/OrganizationLayout";
import { Dropdown } from "@/Components/";
import {
  LabeledInput,
  LabeledMultiSelectAutocomplete,
  Select,
} from "@/Components/Form";
import { Globe, Search, MoreVertical } from "react-feather";
import "react-datepicker/dist/react-datepicker.css";
import { ErrorMessage } from "@/Components/Messages";
import { Tab, Tabs } from "@/Components/Tabs";
import { ActionMenu } from "@/Components/ActionMenu";
import { useAtom } from "jotai";
import { atom } from "jotai";
import { debounce } from "lodash";
import { SCOPE_OPTIONS } from "@/Enums/scopes";
import LayoutIcon from "@/Components/LayoutIcon";
import { IconVerifiedBadge, Percent } from "@/Icons";
import { formatEmissions } from "@/Utilities/Utilities";
import EmissionFactorDialogCenter from "@/Pages/Factors/Partials/EmissionFactorDialogCenter";
import { EmptyState } from "@/Components";

const searchAtom = atom("");
const sortAtom = atom("name.asc");

const scopesAtom = atom(null);

export default function Factors({
  factors,
  global_factors,
  waiting_factors,
  errors,
  message,
  tab,
  ppp,
}) {
  const isAgency = usePage().props?.organization?.data?.is_agency;

  let pagePrefix = "";
  const currentRoute = route()?.current();

  if (currentRoute.startsWith("admin.")) {
    pagePrefix = "admin.";
  } else if (currentRoute.startsWith("agency.")) {
    pagePrefix = "agency.";
  }

  const [search, setSearch] = useAtom(searchAtom);

  const tabIndex = (t) => {
    return (
      {
        company: 0,
        global: 1,
        waiting: 2,
      }[t] || 0
    );
  };

  return (
    <div>
      {message && <p className="mb-2 p-2">{message}</p>}
      {errors?.message && <ErrorMessage>{errors?.message}</ErrorMessage>}

      <Tabs
        items={
          isAgency
            ? ["Your global", "Emma global", "Waiting for verification"]
            : ["Company specific", "Global", "Waiting for verification"]
        }
        current={tabIndex(tab)}
      >
        <Tab>
          <FactorFilter type="company" />

          {factors.data.length === 0 && search === "" && (
            <EmptyState
              title="You have no emission factors"
              text="Get started by clicking 'New emission factor' below."
              buttonText="New emission factor"
              buttonRoute={route(`${pagePrefix}factors.createForm`)}
            />
          )}

          {factors.data.length > 0 && (
            <>
              <div className="mb-2 grid grid-cols-1 gap-2">
                {factors.data.map((f, i) => (
                  <FactorItem factor={f} index={i} key={f.id} />
                ))}
              </div>
              {pagination(factors, "company", ppp)}
            </>
          )}
        </Tab>
        <Tab>
          <FactorFilter type="global" />
          <div className="mb-2 grid grid-cols-1 gap-2">
            {global_factors.data.map((f, i) => (
              <FactorItem factor={f} index={i} key={f.id} />
            ))}
          </div>
          {pagination(global_factors, "global", ppp)}
        </Tab>
        <Tab>
          <FactorFilter type="waiting" />
          {waiting_factors.data.length === 0 && search === "" && (
            <EmptyState
              title="No emission factors waiting for verification"
              text="There are no emission factors from your users waiting for verification."
            />
          )}
          {waiting_factors.data.length > 0 && (
            <>
              <div className="mb-2 grid grid-cols-1 gap-2">
                {waiting_factors.data.map((f, i) => (
                  <FactorItem factor={f} index={i} key={f.id} />
                ))}
              </div>
              {pagination(waiting_factors, "waiting", ppp)}
            </>
          )}
        </Tab>
      </Tabs>
    </div>
  );
}

export const FactorItem = ({ factor }) => {
  const user = usePage().props?.auth?.user?.data || {};
  const organization = usePage().props?.organization?.data || {};
  const agency =
    usePage().props?.agency?.data ||
    (organization?.is_agency ? organization : null);
  const isSuper = user?.is_super;

  let pagePrefix = "";
  const currentRoute = route()?.current();

  if (currentRoute.startsWith("admin.")) {
    pagePrefix = "admin.";
  } else if (currentRoute.startsWith("agency.")) {
    pagePrefix = "agency.";
  }

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const verifiedIcon = (t) => {
    return (
      {
        requested: (
          <img
            className="-mt-1 mr-2 inline"
            src={"/images/arrow_red.svg"}
            alt={verifiedText(t)}
            width="24"
          />
        ),
        denied_verification: null,
        verified: agency ? (
          <IconVerifiedBadge
            className="-mt-1 mr-2 inline"
            size={24}
            title={verifiedText(t)}
          />
        ) : (
          <img
            className="-mt-1 mr-2 inline"
            src={"/images/gidas_badge.svg"}
            alt={verifiedText(t)}
            width="24"
          />
        ),
      }[t] || ""
    );
  };
  const verifiedText = (t) => {
    return (
      {
        requested: "Waiting for verification",
        denied_verification: "Not approved",
        verified: `Verified by ${agency?.name || "Gidås"}`,
      }[t] || ""
    );
  };

  const actionMenuRef = useRef(null);
  const actionMenuItems = [
    {
      label: "Edit emission factor",
      callback: () =>
        Inertia.visit(route(`${pagePrefix}factors.edit`, factor.id)),
    },
    // {
    //   label: factor.is_deleted ? 'Restore factor' : 'Archive emission factor',
    //   callback: () => factor.is_deleted ? handleRestore() : handleDelete()
    // },
  ];
  if (!factor.global || isSuper) {
    actionMenuItems.push({
      label: "Delete emission factor",
      callback: () => setShowDeleteModal(true),
    });
  }

  const global = factor.global ? (
    <div className="order-1">
      <Globe className="-mt-1 mr-2 inline" size={19} />
    </div>
  ) : null;

  return (
    <>
      <InertiaLink
        key={factor.id}
        className="shadow-small flex min-h-[53px] cursor-pointer flex-wrap items-center justify-stretch gap-2 bg-gray-light p-4 md:p-8 md:px-4 md:py-2"
        href={route(`${pagePrefix}factors.edit`, factor.id)}
      >
        {/* left side  */}
        {global}
        <div className="order-2 w-3/4 flex-grow truncate sm:w-auto">
          <span className="truncate text-md font-bold">{factor.name}</span>
          <span className="ml-1 truncate text-typo">per {factor.unit}</span>
        </div>
        {/* right side */}
        <div className="order-5 flex w-[calc(50%-.25rem)] justify-end sm:order-4 sm:w-auto sm:justify-start ">
          <span className="rounded-md bg-blue-lighter px-3 py-2 font-overpass text-sm leading-none">
            {formatEmissions(factor?.co2e, 1)}kg CO₂e
          </span>
        </div>
        {/* {verifiedIcon(factor.verified_state) && ( */}
        <div className="order-4 flex w-[calc(50%-.25rem)] flex-row items-center justify-start sm:order-5 sm:w-16 sm:justify-end">
          <span title={verifiedText(factor.verified_state)}>
            {verifiedIcon(factor.verified_state)}
          </span>
        </div>
        {/* )} */}
        <div className="hover-circle relative z-10 order-3 col-start-12 sm:order-5 sm:ml-2">
          <MoreVertical
            size={24}
            className="shrink-0 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              actionMenuRef.current.toggleMenu();
            }}
          />
          <ActionMenu
            ref={actionMenuRef}
            items={actionMenuItems}
            className="ml-2"
          />
        </div>
      </InertiaLink>
      <EmissionFactorDialogCenter
        factor={factor}
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
      />
    </>
  );
};

const FactorFilter = ({ type }) => {
  const [search, setSearch] = useAtom(searchAtom);
  const [sort, setSort] = useAtom(sortAtom);
  const [scopes, setScopes] = useAtom(scopesAtom);

  const sortOptions = [
    { value: "name.asc", label: "Name A-Ö" },
    { value: "name.desc", label: "Name Ö-A" },
    { value: "created_at.desc", label: "Newest" },
    { value: "created_at.asc", label: "Oldest" },
    { value: "updated_at.desc", label: "Updated most recent" },
    { value: "updated_at.asc", label: "Updated least recent" },
  ];

  const scopeRef = useRef(null);

  const handleSearchAndSort = React.useRef(
    debounce((search, sort, scopes, type) => {
      Inertia.reload({
        only: ["factors", "global_factors", "waiting_factors"],
        data: {
          search: search,
          sort: sort,
          scopes: scopes?.join(","),
          [type]: 1,
        },
      });
    }, 500),
  ).current;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (!params.get("scopes") || scopes === null) {
      const initialSearch = params.get("search") || "";
      const initialSort = params.get("sort") || "";
      const initialScopes = params.get("scopes")
        ? params.get("scopes").split(",")
        : SCOPE_OPTIONS.map((s) => s.value);

      setSearch(initialSearch);
      setSort(initialSort);
      setScopes(initialScopes);

      scopeRef.current.setStateOptions((prev) => {
        return prev.map((scope) => {
          return {
            ...scope,
            checked: initialScopes.includes(scope.value),
          };
        });
      });

      refreshData(initialSearch, initialSort, initialScopes);
    }
  }, []);

  const refreshData = (search, sort, scopes) => {
    handleSearchAndSort.cancel();
    handleSearchAndSort(search, sort, scopes, type);
  };

  return (
    <div className="mb-4 flex flex-col items-end justify-between gap-4 sm:flex-row sm:items-center">
      <div className="flex w-full flex-col gap-4 sm:w-auto sm:flex-row sm:items-end">
        <LabeledMultiSelectAutocomplete
          className="w-full sm:w-auto"
          ref={scopeRef}
          placeholder="All scopes"
          options={SCOPE_OPTIONS}
          defaultValue={scopes || []}
          onSelect={(val) => {
            const values = val.map((v) => v.value);
            setScopes(values);
            refreshData(search, sort, values);
          }}
        />

        <LabeledInput
          value={search}
          type="search"
          placeholder="Search"
          className="w-full sm:w-auto"
          unit={search === "" ? <Search className="relative -top-1" /> : null}
          onChange={(e) => {
            const value = e.target.value;
            setSearch(e.target.value);
            refreshData(value, sort, scopes);
          }}
        />
      </div>

      <Dropdown
        options={sortOptions}
        title="Sort on"
        placeholder="Name A-Ö"
        onSelect={(value) => {
          setSort(value);
          refreshData(search, value, scopes);
        }}
      />
    </div>
  );
};

Factors.layout = (page) => {
  const isAgency = page.props?.organization?.data?.is_agency;

  let pagePrefix = "";
  const currentRoute = route()?.current();

  if (currentRoute.startsWith("admin.")) {
    pagePrefix = "admin.";
  } else if (currentRoute.startsWith("agency.")) {
    pagePrefix = "agency.";
  }

  return (
    <AppLayout title="Emission Factors">
      <OrganizationLayout
        header="Emission Factors"
        byline={
          isAgency
            ? "Create your own global emission factors that can be attached to any one of your clients."
            : "Create emission factors relevant for your business and edit past or future data for the emission factors you created."
        }
        action={{
          label: "New emission factor",
          callback: () =>
            Inertia.visit(route(`${pagePrefix}factors.createForm`)),
        }}
        icon={
          <LayoutIcon>
            <Percent size={40} className="text-black" />
          </LayoutIcon>
        }
      >
        {page}
      </OrganizationLayout>
    </AppLayout>
  );
};

const getActiveLink = (l) => {
  return l.filter((l) => l.active)[0]?.url || false;
};

function pagination(data, ti, ppp = 15) {
  const active_url = getActiveLink(data?.meta?.links || []);
  const mp = data?.meta?.current_page || 1;

  if (!data?.meta) {
    return "";
  }

  return (
    <div className="flex flex-row items-center justify-between py-4">
      {data.meta.last_page > 1 && (
        <div className="flex flex-shrink flex-grow-0">
          {data.meta.links.map((l, i) => (
            <div key={i}>
              {!l.url && <span className="mr-2">{l.label}</span>}
              {l.url && (
                <InertiaLink
                  preserveScroll
                  className={`mr-2 px-3 py-2 ${
                    l.active ? "bg-primary text-white" : ""
                  }`}
                  href={l.url}
                >
                  {l.label}
                </InertiaLink>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-row items-center justify-self-end">
        {/* <span className="mr-2">Per page</span> */}
        <Select
          className="w-32"
          options={[
            { value: "15", label: "Visa 15" },
            { value: "25", label: "Visa 25" },
            { value: "50", label: "Visa 50" },
            { value: "100", label: "Visa 100" },
          ]}
          value={ppp}
          onChange={(e) => {
            Inertia.get(active_url + `&${ti}=${mp}`, {
              // preserveScroll: true,
              ppp: e.target.value,
            });
          }}
        />
      </div>
    </div>
  );
}
